import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatToolbarModule } from '@angular/material/toolbar';
import { TranslateModule } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { UserService } from 'src/app/services/api/users/user.service';
import { ButtonComponent } from 'ui-library';

@Component({
  selector: 'app-edit-password',
  templateUrl: './edit-password.component.html',
  styleUrls: ['./edit-password.component.scss'],
  imports: [
    MatToolbarModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatIconModule,
    MatDialogModule,
    TranslateModule,
    MatInputModule,
    MatButtonModule,
    CommonModule,
    ButtonComponent
  ],
  standalone: true
})

export class EditPasswordComponent implements OnInit, OnDestroy {
  // Services
  private readonly fb: FormBuilder = inject(FormBuilder);
  private readonly userService: UserService = inject(UserService);
  private readonly dialog: MatDialogRef<EditPasswordComponent> = inject(MatDialogRef<EditPasswordComponent>);
  public readonly data: any = inject(MAT_DIALOG_DATA);

  // Variables
  pwdForm: any;
  destroy$ = new Subject<void>();

  passwordCriteria = {
    length: false,
    uppercase: false,
    lowercase: false,
    digit: false,
    noSpaces: false
  };

  reachedCriteria = {
    length: false,
    uppercase: false,
    lowercase: false,
    digit: false,
    noSpaces: false
  };

  constructor() { }

  ngOnInit(): void {
    this.pwdForm = this.fb.group({
      password: ['', Validators.required],
      newPassword: ['', Validators.required],
      _newPassword: ['', Validators.required]
    })
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async savePassword() {
    if (this.pwdForm.status == 'INVALID') {
      return;
    }

    this.userService.verifyUserByPassword({ email: this.data.email, password: this.pwdForm.value.password }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (user) => {
        if (user == null) {
          this.pwdForm.controls["password"].setErrors({ incorrect: true });
        } else {
          if (this.pwdForm.value.newPassword == this.pwdForm.value._newPassword) {
            let pattern: RegExp = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?!.*\s).*$/;

            if (this.pwdForm.value.newPassword.length < 8 || !this.validText(this.pwdForm.value.newPassword, pattern)) {
              this.pwdForm.controls["newPassword"].setErrors({ incorrect: true });
            } else {
              this.dialog.close(this.pwdForm.value.newPassword);
            }
          } else {
            this.pwdForm.controls["_newPassword"].setErrors({ incorrect: true });
          }
        }
      },
      error: (err) => {
        console.error(err);
      }
    });
  }

  checkPasswordCriteria() {
    const password = this.pwdForm.get('newPassword').value;
    this.passwordCriteria.length = password.length >= 8;
    this.passwordCriteria.uppercase = /[A-Z]/.test(password);
    this.passwordCriteria.lowercase = /[a-z]/.test(password);
    this.passwordCriteria.digit = /\d/.test(password);
    this.passwordCriteria.noSpaces = !/\s/.test(password);
    this.updateReachedCriteria();
  }

  updateReachedCriteria() {
    for (const key of Object.keys(this.passwordCriteria) as (keyof typeof this.passwordCriteria)[]) {
      if (this.passwordCriteria[key]) this.reachedCriteria[key] = true;
    }
  }

  validText(text: string, pattern: RegExp) {
    return String(text).match(pattern);
  }

  close() {
    this.dialog.close();
  }
}
